import { faCalendarClock } from "@fortawesome/pro-regular-svg-icons";
import { max } from "date-fns";

import { Feature, UISubscriptionStatus, UserSubscriptionAction } from "common/types";
import { toDate, toISODateTime, toRelativeDate } from "common/utils/dates";
import { useBuildNext, useSearchState } from "common/router/hooks";
import Icon from "common/components/Icon";

import { minSelectableDeliveryDate, minSelectableNextOrderDate } from "client/subscriptions/utils";
import {
  OnboardingDetailSubscriptionStepParams,
  OnboardingSubscriptionNextOrderDateStepParams,
} from "client/onboarding/types";
import { ModalStatus } from "client/common/types";
import Button from "client/common/components/buttons/Button";
import { useFeatures } from "client/clinics/hooks";
import { useSubscriptionQuery, useUpdateSubscriptionMutation } from "client/subscriptions/hooks";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import Error404Page from "client/common/components/errors/Error404Page";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";
import { Paths } from "client/routes";

import UpdateNextOrderDateModal from "../modals/UpdateNextOrderDateModal";
import StepLayout from "../OnboardingSubscriptionsStepLayout";

import styles from "../../styles/OnboardingSubscriptionNextOrderDateStep.module.less";

const OnboardingSubscriptionNextOrderDateStep = ({
  id,
  onNext,
  isLoadingNext,
  stepper,
  rootDest,
}: OnboardingDetailSubscriptionStepParams) => {
  const buildNext = useBuildNext();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const [params, setParams] = useSearchState<Paths["onboarding"]["subscriptions"]["detail"]>({
    dateModalIsVisible: ModalStatus.Closed,
  });
  const invalidateOnboarding = useInvalidateOnboarding();
  const {
    mutate: updateSubscription,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateSubscriptionMutation(id, {
    onSuccess: () => {
      invalidateOnboarding();
      onNext();
    },
  });
  const { data: subscription, isLoading: isSubscriptionLoading } = useSubscriptionQuery(id);

  if (isSubscriptionLoading) {
    return <LoadingContainer />;
  }
  if (!subscription) {
    return (
      <Error404Page
        title="Autoship Not Found!"
        description="We could not find that Autoship"
        buttonText="Update other Autoships"
        buttonLink={buildNext(rootDest)}
      />
    );
  }

  const setModalVisibility = (
    param: keyof OnboardingSubscriptionNextOrderDateStepParams,
    visibility: ModalStatus,
  ) => setParams({ ...params, [param]: visibility });
  const canContinue = !isUpdating && !isLoadingNext;

  // Imported subscriptions are activated via RESUME; an already-active subscription is
  // modified instead so re-activating it doesn't error.
  const isImported = subscription.visible_status === UISubscriptionStatus.Imported;

  // v1 RESUME requires a next order date of tomorrow or later. Imported subs can still carry a
  // today/past date at activation, so floor it; an active sub keeps its stored date since a
  // MODIFY may resubmit the current date unchanged.
  const v1NextOrderDate = (): Date | undefined => {
    const storedNextOrderDate = subscription.next_order_date
      ? new Date(subscription.next_order_date)
      : undefined;
    if (!isImported) {
      return storedNextOrderDate;
    }
    const flooredNextOrderDate = minSelectableNextOrderDate();
    return storedNextOrderDate
      ? max([storedNextOrderDate, flooredNextOrderDate])
      : flooredNextOrderDate;
  };

  // In v2 the imported sub already carries an EDB (set at import), so show it. Old-world shows NOD.
  const displayDate = isEdbEnabled ? subscription.expected_delivery_by : v1NextOrderDate();

  const openDateModal = () => setModalVisibility("dateModalIsVisible", ModalStatus.Open);

  const handleContinue = () => {
    const action = isImported ? UserSubscriptionAction.Resume : UserSubscriptionAction.Modify;
    if (isEdbEnabled) {
      // v2 RESUME requires an expected_delivery_by at least the min lead-time out. A v2
      // subscription should always carry an EDB set at import, so a missing one isn't an
      // expected state — it's only reachable because the FE type still allows a null EDB
      // (this can be tightened to always require an EDB once all clinics have migrated).
      // If the EDB is missing or inside the lead-time window, make the client select a
      // valid delivery date before continuing.
      if (
        !subscription.expected_delivery_by ||
        new Date(subscription.expected_delivery_by) < minSelectableDeliveryDate()
      ) {
        openDateModal();
        return;
      }
      updateSubscription({
        action,
        expected_delivery_by: subscription.expected_delivery_by,
        frequency: subscription.active_frequency.frequency,
        interval_unit: subscription.active_frequency.interval_unit,
      });
      return;
    }
    const nextOrderDate = v1NextOrderDate();
    updateSubscription({
      action,
      next_order_date: nextOrderDate ? toISODateTime(nextOrderDate) : undefined,
      frequency: subscription.active_frequency.frequency,
      interval_unit: subscription.active_frequency.interval_unit,
    });
  };

  return (
    <StepLayout stepper={stepper}>
      {!!updateError && (
        <FetchErrorAlert
          error={updateError}
          fieldErrors={[updateError.errors.action, updateError.errors.next_order_date]}
        />
      )}
      <StepLayout.Title>
        {isEdbEnabled
          ? "Great! Does this date work for your next estimated delivery?"
          : "Great! Does this date work to ship your next order?"}
      </StepLayout.Title>
      {displayDate && (
        <div className={styles.banner}>
          <div className={styles.icon}>
            <Icon icon={faCalendarClock} />
          </div>
          <div className={styles.content}>
            <div className={styles.date}>{toDate(displayDate)}</div>
            <div className={styles.relative}>{toRelativeDate(displayDate)}</div>
          </div>
        </div>
      )}
      <StepLayout.Footer>
        <Button variant="secondary" onClick={openDateModal}>
          I need it another time...
        </Button>
        <Button
          onClick={handleContinue}
          isLoading={isUpdating || isLoadingNext}
          disabled={!canContinue}
        >
          Continue
        </Button>
      </StepLayout.Footer>
      <UpdateNextOrderDateModal
        isVisible={params.dateModalIsVisible === ModalStatus.Open}
        onClose={() => setModalVisibility("dateModalIsVisible", ModalStatus.Closed)}
        subscriptionId={id}
        initialNextOrderDate={subscription.next_order_date}
        initialExpectedDeliveryBy={subscription.expected_delivery_by}
      />
    </StepLayout>
  );
};

export default OnboardingSubscriptionNextOrderDateStep;
